import axios, {AxiosRequestConfig, AxiosResponse, Method} from 'axios';
import HttpErrorResponseModel from '../models/HttpErrorResponseModel';
import IStore from '../models/IStore';
import {AuthUtility} from '@/auth';
import configuration from './config/configuration';
import { isManagementViewEnabled } from './EnvironmentUtility';
import { requestViaWorker } from './HttpWorkerClient';

export const API_GATEWAY = configuration.apiGateway;


export enum RequestMethod {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
  Options = 'OPTIONS',
  Head = 'HEAD',
  Patch = 'PATCH',
}

const USE_HTTP_WORKER: boolean = import.meta.env.VITE_USE_HTTP_WORKER === 'true';

export async function get(
  state: IStore,
  endpoint: string,
  params?: any,
  requestConfig?: AxiosRequestConfig
): Promise<AxiosResponse | HttpErrorResponseModel> {
  const paramsConfig: AxiosRequestConfig | undefined = params ? { params } : undefined;

  return _request(
    {
      url: endpoint,
      method: RequestMethod.Get,
    },
    {
      ...paramsConfig,
      ...requestConfig,
      headers: await headerAuthorization(state),
    }
  );
}

export async function post(state: IStore, endpoint: string, data?: any): Promise<AxiosResponse | HttpErrorResponseModel> {
  const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

  return _request(
    {
      url: endpoint,
      method: RequestMethod.Post,
    },
    {
      ...config,
      headers:await headerAuthorization(state),
    }
  );
}

export async function patch(state: IStore, endpoint: string, data?: any): Promise<AxiosResponse | HttpErrorResponseModel> {
  const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

  return _request(
    {
      url: endpoint,
      method: RequestMethod.Patch,
    },
    {
      ...config,
      headers: await headerAuthorization(state),
    }
  );
}

export async function put(state: IStore, endpoint: string, data?: any): Promise<AxiosResponse | HttpErrorResponseModel> {
  const config: AxiosRequestConfig | undefined = data ? { data } : undefined;

  return _request(
    {
      url: endpoint,
      method: RequestMethod.Put,
    },
    {
      ...config,
      headers: await headerAuthorization(state),
    }
  );
}

export async function del(state: IStore, endpoint: string): Promise<AxiosResponse | HttpErrorResponseModel> {
  return _request(
    {
      url: endpoint,
      method: RequestMethod.Delete,
    },
    {
      headers: await headerAuthorization(state),
    }
  );
}

// Send GET request without Authorization headers
export async function getNoAuth(state: IStore, endpoint: string, params?: any, requestConfig?: AxiosRequestConfig
): Promise<AxiosResponse | HttpErrorResponseModel> {
  const paramsConfig: AxiosRequestConfig | undefined = params ? { params } : undefined;
  return _request(
    {
      url: endpoint,
      method: RequestMethod.Get,
    },
    {
      ...paramsConfig,
      ...requestConfig,
      //  headers: await headerAuthorization(state),
    }
  );
}

export async function _request(restRequest: Partial<Request>, config?: AxiosRequestConfig): Promise<AxiosResponse | HttpErrorResponseModel> {
  if (!Boolean(restRequest.url)) {
    console.error(`Received ${restRequest.url} which is invalid for a endpoint url`);
  }

  try {
    const axiosRequestConfig: AxiosRequestConfig = {
      ...config,
      method: restRequest.method as Method,
      url: API_GATEWAY == "" ? restRequest.url : `${API_GATEWAY}/${restRequest.url}`,
      headers: {
        ...config?.headers,
      },
    };

    const requestPromise = USE_HTTP_WORKER
      ? requestViaWorker(axiosRequestConfig).catch(() => axios(axiosRequestConfig))
      : axios(axiosRequestConfig);

    const [axiosResponse] = await Promise.all([requestPromise, _delay()]);

    const transportError = _mapTransportErrorResponse(axiosResponse, restRequest);
    if (transportError) {
      return transportError;
    }

    const businessError = _mapBusinessErrorResponse(axiosResponse, restRequest);
    if (businessError) {
      return businessError;
    }

    return {
      ...axiosResponse,
    };
  } catch (error:any) {
    return _mapCaughtError(error, restRequest);
  }
}

function _mapTransportErrorResponse(
  axiosResponse: AxiosResponse,
  restRequest: Partial<Request>
): HttpErrorResponseModel | undefined {
  const { status, data, request } = axiosResponse;
  if (status >= 200 && status < 300) {
    return undefined;
  }

  const responseData: any = data;
  const errors = _extractErrors(responseData);

  return _fillInErrorWithDefaults(
    {
      status,
      message: responseData && responseData !== '' && typeof responseData === 'string' ? responseData : 'Request failed',
      errors,
      url: request ? (request as any).responseURL : restRequest.url,
      raw: axiosResponse,
    },
    restRequest
  );
}

function _mapBusinessErrorResponse(
  axiosResponse: AxiosResponse,
  restRequest: Partial<Request>
): HttpErrorResponseModel | undefined {
  const { status, data, request } = axiosResponse;
  if (data?.success !== false) {
    return undefined;
  }

  return _fillInErrorWithDefaults(
    {
      status,
      message: Array.isArray(data.errors) ? data.errors.join(' - ') : 'Request failed',
      errors: Array.isArray(data.errors) ? data.errors : [data.errors],
      url: request ? request.responseURL : restRequest.url,
      raw: axiosResponse,
    },
    restRequest
  );
}

function _mapCaughtError(error: any, restRequest: Partial<Request>): HttpErrorResponseModel {
  // Request was made and server responded.
  if (error?.response) {
    const { status, data } = error.response;
    const errors = _extractErrors(data);

    return _fillInErrorWithDefaults(
      {
        status,
        message: data && data != "" && typeof data === 'string' ? data : error.message,
        errors,
        url: error.request?.responseURL,
        raw: error.response,
      },
      restRequest
    );
  }

  // Request failed before receiving a response.
  return _fillInErrorWithDefaults(
    {
      status: 0,
      message: error.message,
      errors: [error.message],
      url: restRequest.url!,
      raw: error,
    },
    restRequest
  );
}

function _extractErrors(data: any): any[] {
  if (data && typeof data === 'object' && data.hasOwnProperty('errors') && Array.isArray(data.errors)) {
    return [data, ...data.errors];
  }

  return [data];
}

function _fillInErrorWithDefaults(error: Partial<HttpErrorResponseModel>, request: Partial<Request>): HttpErrorResponseModel {
  const model = new HttpErrorResponseModel();

  model.status = error.status || 0;
  model.message = error.message || 'Error requesting data';
  model.errors = error.errors!.length ? error.errors! : ['Error requesting data'];
  model.url = error.url || request.url!;
  model.raw = error.raw;

  // Remove anything with undefined or empty strings.
  model.errors = model.errors.filter(Boolean);

  return model;
}

/**
 * We want to show the loading indicator to the user but sometimes the api
 * request finished too quickly. This makes sure there the loading indicator is
 * visual for at least a given time.
 *
 * @param duration
 * @returns {Promise<void>}
 * @private
 */
function _delay(duration: number = 250): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, duration));
}

async function headerAuthorization(state: IStore) {
  const entraIdIDToken:any = { Authorization: `Bearer ${localStorage.getItem('id_token')}` };
  const bearerHeader:any = isManagementViewEnabled() ? AuthUtility.getBearerHeader() : entraIdIDToken;
  
  if (isTokenExpired(bearerHeader)) {
    const resendResponse = await AuthUtility.getTokenFromRefreshToken();
    if (resendResponse) {
      return isManagementViewEnabled() ? AuthUtility.getBearerHeader() : entraIdIDToken;
    }else{
      window.location.href = '/';
    }
  }
  return bearerHeader;
}

export function isTokenExpired(tokenObject: { Authorization: string }): boolean {
  const token = tokenObject?.Authorization?.split(' ')[1]; 
  if (!token) {
    console.error('No token found in Authorization header');
    return true;
  }

  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    console.error('Invalid JWT token format');
    return true;
  }
  
  let tokenPayload;
  try {
    tokenPayload = JSON.parse(decodeBase64Url(tokenParts[1])); 
  } catch (e) {
    console.error('Failed to decode token:', e);
    return true;
  }
  const expirationTime = tokenPayload.exp * 1000;
  return Date.now() >= expirationTime; 
 } 

 function decodeBase64Url(base64Url: string): string {
  if (!base64Url || typeof base64Url !== 'string') {
    throw new Error('Invalid base64Url input');
  }
  
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
  return atob(paddedBase64);
}